import { motion } from 'framer-motion';
import { useTheme } from '../ThemeContext';

const ADVANTAGES = [
  {
    title: 'In-House R&D',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
    points: ['Full-stack concept-to-product ownership', 'Proprietary hardware + firmware development', 'Continuous innovation pipeline'],
  },
  {
    title: 'Manufacturing',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
    ),
    points: ['State-of-the-art production facilities', 'Quality control at every manufacturing stage', 'Scalable production capacity'],
  },
  {
    title: 'Firmware + App',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
      </svg>
    ),
    points: ['Proprietary OS and device firmware', 'Native iOS & Android mobile app', 'OTA update capability'],
  },
  {
    title: 'Service Team',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    points: ['Certified installation technicians', 'Dedicated post-sales support team', 'WhatsApp + phone support'],
  },
  {
    title: '2-Year Warranty',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
    points: ['Industry-leading 2-year replacement warranty', 'AMC plans for extended coverage', 'No-questions-asked hardware replacement'],
  },
];

function SectionLabel({ children }) {
  return <span className="text-[9px] md:text-[10px] font-mono uppercase tracking-widest text-bronze-400/70 mb-3 block">{children}</span>;
}

export default function OEMAdvantage() {
  const { theme } = useTheme();
  return (
    <section className="py-16 md:py-20 px-4 sm:px-8 md:px-14 bg-theme transition-colors duration-700">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-12 flex flex-col md:flex-row md:items-end md:justify-between gap-4"
        >
          <div>
            <SectionLabel>Our Ecosystem</SectionLabel>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-light text-theme">
              The IOTIQ <span className={`italic ${theme === 'light' ? 'text-bronze-600' : 'text-bronze-300'}`}>Advantage.</span>
            </h2>
          </div>
          <p className="text-sm text-theme-secondary max-w-sm leading-relaxed font-light">
            Unlike traditional integrators, IOTIQ controls every layer — from chip design to the app on your phone.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {ADVANTAGES.map((adv, i) => (
            <motion.div
              key={adv.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
              whileHover={{ y: -12, scale: 1.02, boxShadow: '0 20px 40px rgba(139, 110, 45, 0.1)' }}
              className="glass-surface-v2 p-5 md:p-6 rounded-2xl border border-bronze-500/10 hover:border-bronze-500/30 transition-all duration-500 group"
            >
              <div className="w-9 h-9 rounded-full bg-bronze-500/10 flex items-center justify-center text-bronze-400 mb-4 group-hover:bg-bronze-500/20 transition-colors duration-500">
                {adv.icon}
              </div>
              <h3 className={`text-sm md:text-base font-mono font-medium uppercase tracking-wide mb-3 transition-colors ${
                theme === 'light' ? 'text-bronze-600 group-hover:text-bronze-700' : 'text-bronze-300 group-hover:text-bronze-200'
              }`}>{adv.title}</h3>
              <ul className="space-y-2">
                {adv.points.map((pt) => (
                  <li key={pt} className="text-[10px] md:text-[11px] text-theme-secondary flex items-start gap-2 leading-snug">
                    <div className="w-1 h-1 rounded-full bg-bronze-500/50 mt-1.5 flex-shrink-0" />
                    {pt}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Warranty badge */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mt-8 flex justify-center"
        >
          <div className="inline-flex items-center gap-3 glass-surface-v2 border border-bronze-500/20 px-5 py-3 rounded-full">
            <div className="w-5 h-5 flex items-center justify-center text-bronze-400">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <div>
              <span className="text-[10px] font-mono uppercase tracking-widest text-bronze-400">2-Year Replacement Warranty</span>
              <span className="text-9px text-theme-muted font-mono block">Backed by OEM quality assurance</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}